import {
  FederationProvidersEnum,
  ICognitoUser,
  IDomainFederation,
  IOrganization,
  TVerificationTypes,
} from "@tedivo/tvd-api-models";
import HttpClient, { IResponseModel } from "../HttpClient";

const AccountsService = {
  createNewAccount: async (
    data: IAuthCreateAccountFields & { recaptchaToken: string },
  ): Promise<IResponseModel<ICreateAccountOutput>> => {
    const client = new HttpClient();
    return await client.request<ICreateAccountOutput>({
      method: "POST",
      url: "accounts/create",
      data,
      isAnonymous: true,
    });
  },

  resendVerificationEmail: async (
    hash: string,
    email: string,
  ): Promise<IResponseModel<unknown>> => {
    const client = new HttpClient();
    return await client.request<unknown>({
      method: "POST",
      url: "accounts/resendVerificationEmail",
      data: { hash, email },
      isAnonymous: true,
    });
  },

  verifyEmail: async (
    data: IAuthVerifyEmail,
  ): Promise<IResponseModel<IAuthVerifyEmailOutput>> => {
    const client = new HttpClient();
    return await client.request<IAuthVerifyEmailOutput>({
      method: "POST",
      url: "accounts/verify",
      data,
      isAnonymous: true,
    });
  },

  notifyNewPassword: async (): Promise<IResponseModel<void>> => {
    const client = new HttpClient();
    return await client.request<void>({
      method: "POST",
      url: "accounts/notifyNewPassword",
    });
  },

  editMyDetails: async (
    data: Pick<ICognitoUser, "name" | "familyName" | "email">,
  ): Promise<IResponseModel<ISimpleResponse>> => {
    const client = new HttpClient();
    return await client.request<ISimpleResponse>({
      method: "PUT",
      url: "accounts/editMyDetails",
      data,
    });
  },

  getMyDetails: async (): Promise<IResponseModel<Partial<ICognitoUser>>> => {
    const client = new HttpClient();
    return await client.request<Partial<ICognitoUser>>({
      method: "GET",
      url: "accounts/getMyDetails",
    });
  },

  getUserDetailsByAdmin: async (
    orgId: string,
    userId: string,
  ): Promise<IResponseModel<Partial<ICognitoUser>>> => {
    const client = new HttpClient();
    return await client.request<Partial<ICognitoUser>>({
      method: "GET",
      url: `/accounts/admin/getDetails/${orgId}/${encodeURIComponent(userId)}`,
    });
  },

  getMyOrganizationUsers: async (
    orgId: string,
  ): Promise<IResponseModel<Record<string, string>>> => {
    const client = new HttpClient();
    return await client.request<Record<string, string>>({
      method: "GET",
      url: `accounts/usersNamesByIdOfOrganization/${orgId || ""}`,
    });
  },

  getOrgFederations: async (): Promise<IResponseModel<IDomainFederation[]>> => {
    const client = new HttpClient();
    return await client.request<IDomainFederation[]>({
      method: "GET",
      url: `organizations/getMyFederations`,
    });
  },

  getLoginOptions: async (
    email: string,
  ): Promise<IResponseModel<{ provider: string }>> => {
    const client = new HttpClient();
    return await client.request<{ provider: string }>({
      method: "POST",
      url: `accounts/getLoginOptions`,
      data: { email },
      isAnonymous: true,
    });
  },

  passEntraIdAccessToken: async (
    token: string,
    email: string,
  ): Promise<IResponseModel<{ email: string; customAuthCode: string }>> => {
    const client = new HttpClient();
    return await client.request<{ email: string; customAuthCode: string }>({
      method: "POST",
      url: "accounts/passEntraIdIdToken",
      data: { token, email },
      isAnonymous: true,
    });
  },

  changeMyPassword: async (
    password: string,
  ): Promise<IResponseModel<ISimpleResponse>> => {
    const client = new HttpClient();
    return await client.request<ISimpleResponse>({
      method: "PUT",
      url: "accounts/changeMyPassword",
      data: { password },
    });
  },

  changeOrganizationSelfDetails: async (
    name: string,
    mfaForAllUsers: boolean,
  ): Promise<IResponseModel<unknown>> => {
    const client = new HttpClient();
    return await client.request<unknown>({
      method: "PUT",
      url: "accounts/organizationSelf",
      data: { name, mfaForAllUsers },
    });
  },

  deleteMyUserAccount: async (
    mfaCode: string,
    accessToken: string,
  ): Promise<IResponseModel<unknown>> => {
    const client = new HttpClient();
    return await client.request<unknown>({
      method: "DELETE",
      url: "accounts/deleteMyAccount",
      data: { mfaCode, accessToken },
    });
  },

  deleteMyOrgAccount: async (
    mfaCode: string,
    accessToken: string,
  ): Promise<IResponseModel<unknown>> => {
    const client = new HttpClient();
    return await client.request<unknown>({
      method: "DELETE",
      url: "accounts/deleteMyOrgAccount",
      data: { mfaCode, accessToken },
    });
  },

  updateUserMarketingPreferences: async (
    acceptsMarketing: boolean,
  ): Promise<IResponseModel<ISimpleResponse | ICreateOutputError>> => {
    const client = new HttpClient();
    return await client.request<ISimpleResponse | ICreateOutputError>({
      method: "PUT",
      url: `accounts/marketingPreferences`,
      data: {
        acceptsMarketing,
      },
    });
  },

  ssoExchangeCodeForTokens: async (
    code: string,
  ): Promise<IResponseModel<IOauth2TokenResponse>> => {
    const client = new HttpClient();
    return await client.request<IOauth2TokenResponse>({
      method: "POST",
      url: "sso/saml-code",
      data: { code },
      isAnonymous: true,
    });
  },
};

export default AccountsService;

export interface IAuthCreateAccountFields {
  email: string;
  name: string;
  familyName: string;
  orgName: string;
  acceptsMarketing?: boolean;
}

export interface IAuthVerifyEmail {
  email: string;
  hash: string;
  subject?: TVerificationTypes;
}

export interface IAuthVerifyEmailOutput {
  isVerified: boolean;
  password?: string;
}

interface ISimpleResponse {
  id?: string;
}

export interface IMergeOrganizationsCandidates {
  organizations: Partial<IOrganization>[];
}

type ICreateOutputError = { id?: never; code: string; message: string };

export interface ICreateAccountOutput {
  hash: string;
}

export interface IOauth2TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  id_token: string;
  action?: string;
  federationProvider?: FederationProvidersEnum;
}
